import { useSignal } from '@preact/signals'

import { signin, signup } from '@/libs/AuthClient'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const NICKNAME_RE = /^[a-zA-Z0-9_-]{3,30}$/

interface AuthScreenProps {
  onAuthenticated: () => void
}

function validateEmail(email: string): string {
  if (email.length > 254 || !EMAIL_RE.test(email)) {
    return 'Please enter a valid email address.'
  }

  return ''
}

function validatePassword(password: string): string {
  if (password.length < 12 || password.length > 128) {
    return 'Password must be 12 to 128 characters long.'
  }
  if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
    return 'Password must contain at least one letter and one digit.'
  }

  return ''
}

function validateNickname(nickname: string): string {
  if (!NICKNAME_RE.test(nickname)) {
    return 'Nickname must be 3 to 30 characters (letters, digits, - or _).'
  }

  return ''
}

export function AuthScreen({ onAuthenticated }: Readonly<AuthScreenProps>) {
  const busy = useSignal(false)
  const email = useSignal('')
  const error = useSignal('')
  const mode = useSignal<'signin' | 'signup'>('signin')
  const nickname = useSignal('')
  const password = useSignal('')

  function switchMode(next: 'signin' | 'signup') {
    mode.value = next
    error.value = ''
  }

  async function handleSubmit(event: Event) {
    event.preventDefault()
    if (busy.value) {
      return
    }

    error.value = validateEmail(email.value)
    if (error.value === '') {
      error.value = validatePassword(password.value)
    }
    if (error.value === '' && mode.value === 'signup') {
      error.value = validateNickname(nickname.value)
    }
    if (error.value !== '') {
      return
    }

    busy.value = true
    const result =
      mode.value === 'signup'
        ? await signup(nickname.value, email.value, password.value)
        : await signin(email.value, password.value)
    busy.value = false

    if (!result.ok) {
      error.value = result.error ?? 'Something went wrong. Please try again.'

      return
    }

    onAuthenticated()
  }

  return (
    <form class="auth-card" onSubmit={handleSubmit}>
      <h1>Bound For</h1>
      <div class="auth-tabs">
        <button class={mode.value === 'signin' ? 'active' : ''} onClick={() => switchMode('signin')} type="button">
          Sign in
        </button>
        <button class={mode.value === 'signup' ? 'active' : ''} onClick={() => switchMode('signup')} type="button">
          Sign up
        </button>
      </div>
      {mode.value === 'signup' && (
        <input
          autocomplete="nickname"
          id="nickname"
          name="nickname"
          onInput={event => (nickname.value = event.currentTarget.value)}
          placeholder="Nickname"
          type="text"
          value={nickname.value}
        />
      )}
      <input
        autocomplete="email"
        id="email"
        name="email"
        onInput={event => (email.value = event.currentTarget.value)}
        placeholder="Email"
        type="email"
        value={email.value}
      />
      <input
        autocomplete={mode.value === 'signup' ? 'new-password' : 'current-password'}
        id="password"
        name="password"
        onInput={event => (password.value = event.currentTarget.value)}
        placeholder="Password"
        type="password"
        value={password.value}
      />
      {error.value !== '' && <p class="auth-error">{error.value}</p>}
      <button disabled={busy.value} type="submit">
        {busy.value ? 'Please wait…' : mode.value === 'signin' ? 'Sign in' : 'Create account'}
      </button>
    </form>
  )
}
